import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  IListingFilterOptions,
  IListingFilters,
  IListingPage,
  IListingQuery,
  IListingRow,
  IStatusCounter,
  IStatusCountWire,
  STATUS_COUNT_CODES
} from './item-listing.models';

/**
 * Client for the v2 listing reads (HUBX-8479). Four entry points over one server-side filter
 * block (HUBX-8485), each fetched exactly when its inputs change — see the design page
 * "Listing › When the client calls what":
 *
 * - `GET /items` (HUBX-8472): paging, sort, tab, filters.
 * - `GET /items/status-counts` (HUBX-8473): filters only — never on a tab change or page turn.
 * - `GET /items?ids=` (HUBX-8488): the post-save refill — by identity, never through the filters.
 * - `GET /items/filter-options` (HUBX-8487): no parameters — the vendor's whole catalogue.
 *
 * Deliberately separate from ItemWriteService: that one is the write boundary and caches
 * lookups; nothing here is cached, because every one of these answers moves with the data.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemListingService {
  private readonly baseUrl = environment.itemDomainBaseUrl;

  constructor(private http: HttpClient) { }

  /** One page of 25. A page past the end is a legal request: empty `items`, the real total,
   *  and a `currentPage` that says which page was served. */
  getPage(query: IListingQuery): Observable<IListingPage> {
    let params = filterParams(query);
    if (query.status) { params = params.set('status', query.status); }
    if (query.sortDirection) { params = params.set('sortDirection', query.sortDirection); }
    if (query.page != null) { params = params.set('page', String(query.page)); }
    return this.http.get<IListingPage>(this.baseUrl + 'items', { params });
  }

  /**
   * The post-save refill (HUBX-8488): the named rows, complete and in listing shape, so the
   * grid patches them in place instead of refetching the page — which would replace every row
   * and lose the per-row save marks (HUBX-8478).
   *
   * Reads by identity: `ids` is the only parameter, so a row the save pushed out of the active
   * filter still comes back. An id the server does not return (deleted, or out of a Seller's
   * read scope) is simply absent — never a 404.
   *
   * No ids, no request: an empty `ids` value is a 400 from the binder. Duplicates are sent
   * once. The server caps a refill at 100 ids; the page is 25 rows, so a save never nears it.
   */
  getByIds(ids: readonly string[]): Observable<IListingRow[]> {
    const unique = [...new Set(ids)].filter(id => !!id);
    if (!unique.length) { return of([]); }
    let params = new HttpParams();
    for (const id of unique) { params = params.append('ids', id); }
    return this.http.get<{ items: IListingRow[] }>(this.baseUrl + 'items', { params }).pipe(map(body => body.items));
  }

  /**
   * The tab counters. Takes the whole query for the caller's convenience but sends the
   * FILTERS only: status is exactly the predicate the counters drop (a tab change must not
   * move them), and paging or sorting cannot change an aggregate.
   *
   * The server owns the shape of the strip — eight rows, All included, zeros included — so a
   * missing bucket is NOT synthesised here; nothing about a tab is recomputed on the client.
   * An ordinal this build does not know is dropped rather than drawn as a nameless tab.
   */
  getStatusCounts(query: IListingQuery): Observable<IStatusCounter[]> {
    return this.http.get<IStatusCountWire[]>(this.baseUrl + 'items/status-counts', { params: filterParams(query) }).pipe(
      map(rows => rows
        .filter(row => STATUS_COUNT_CODES[row.status] !== undefined)
        .map(row => ({ tab: STATUS_COUNT_CODES[row.status], code: row.status, count: row.count })))
    );
  }

  /**
   * The choices of every control on the filter bar — category tree, manufacturers,
   * promotions, restrictions, ExW — over the session vendor's whole catalogue (the impersonated
   * vendor for an operator), every status, archived included. The route takes no parameters:
   * no filter, tab, search or page moves a list (BE 185cbee).
   */
  getFilterOptions(): Observable<IListingFilterOptions> {
    return this.http.get<IListingFilterOptions>(this.baseUrl + 'items/filter-options');
  }
}

/** The filter bar as a query string, shared by every listing read. Blank members are omitted
 *  rather than sent empty — a blank `q` in particular must never reach the server as a
 *  search, since for ADMIN/VM a search is what unscopes the query to every vendor. */
function filterParams(filters: IListingFilters): HttpParams {
  let params = new HttpParams();
  // Repeated parameter (?categoryIds=a&categoryIds=b): ASP.NET Core binds it to Guid[]
  // with no custom binder.
  for (const id of filters.categoryIds ?? []) {
    if (id) { params = params.append('categoryIds', id); }
  }
  if (filters.manufacturerId) { params = params.set('manufacturerId', filters.manufacturerId); }
  if (filters.promoId) { params = params.set('promoId', filters.promoId); }
  if (filters.restrictions) { params = params.set('restrictions', filters.restrictions); }
  if (filters.exw != null) { params = params.set('exw', String(filters.exw)); }
  const q = filters.q?.trim();
  if (q) { params = params.set('q', q); }
  return params;
}
